import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  BarChart3,
  ClipboardList,
  Download,
  FileText,
  FileStack,
  Star,
  Users,
} from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useAuth } from "../AuthContext";
import { apiFetch } from "../utils/api";
import { actionFailureMessage } from "../utils/actionFeedback";
import DashboardLayout from "../components/layout/DashboardLayout";
import RecruiterSidebar from "../components/layout/RecruiterSidebar";

const RANGE_OPTIONS = [
  { label: "Last 7 days", value: "7" },
  { label: "Last 30 days", value: "30" },
  { label: "Last 90 days", value: "90" },
  { label: "All time", value: "all" },
];

export function ReportsPanel() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [history, setHistory] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [selectedJobId, setSelectedJobId] = useState("all");
  const [range, setRange] = useState("30");
  const [activeTab, setActiveTab] = useState("job");

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user) return;

    const loadReports = async () => {
      setFetching(true);

      try {
        const [jobsRes, historyRes] = await Promise.all([
          apiFetch("/jobs?include_archived=true"),
          apiFetch("/jobs/scan-history?limit=100"),
        ]);
        const jobsData = await jobsRes.json();
        const historyData = await historyRes.json();

        if (jobsRes.ok) {
          setJobs(Array.isArray(jobsData) ? jobsData : []);
        } else {
          toast.error(actionFailureMessage("Load report jobs", jobsData?.detail));
        }

        if (historyRes.ok) {
          setHistory(historyData.history || []);
        } else {
          toast.error(actionFailureMessage("Load reports", historyData?.detail));
        }
      } catch {
        toast.error(actionFailureMessage("Load reports"));
      } finally {
        setFetching(false);
      }
    };

    loadReports();
  }, [user]);

  const formatDate = (value) => {
    if (!value) return "Not available";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "Not available";

    return date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const scoreTone = (score) => {
    if (score >= 75) return "border-emerald-200 bg-emerald-50 text-emerald-700";
    if (score >= 55) return "border-amber-200 bg-amber-50 text-amber-700";
    return "border-rose-200 bg-rose-50 text-rose-700";
  };

  const filteredRuns = useMemo(() => {
    const now = new Date();
    const rangeDays = range === "all" ? null : Number(range);

    return history.filter((run) => {
      const runDate = new Date(run.scanned_at);
      if (Number.isNaN(runDate.getTime())) return false;

      const matchesJob = selectedJobId === "all" || String(run.job?.id) === String(selectedJobId);
      if (!matchesJob) return false;
      if (!rangeDays) return true;

      return (now - runDate) / (1000 * 60 * 60 * 24) <= rangeDays;
    });
  }, [history, range, selectedJobId]);

  const reportRows = useMemo(() => {
    return filteredRuns.flatMap((run) =>
      (run.ranked_candidates || []).map((candidate) => ({
        ...candidate,
        job_id: run.job?.id,
        job_name: run.job?.name || "Untitled job",
        scanned_at: run.scanned_at,
        history_id: run.history_id,
      }))
    );
  }, [filteredRuns]);

  const stats = useMemo(() => {
    const scanned = reportRows.length;
    const shortlisted = reportRows.filter((candidate) => candidate.is_shortlisted).length;
    const aiEnriched = reportRows.filter((candidate) =>
      candidate.ai_summary || (candidate.llm_strengths || []).length || (candidate.llm_gaps || []).length
    ).length;
    const averageScore = scanned
      ? Math.round(reportRows.reduce((sum, candidate) => sum + (candidate.match_score || 0), 0) / scanned)
      : 0;
    const topScore = scanned
      ? Math.round(Math.max(...reportRows.map((candidate) => candidate.match_score || 0)))
      : 0;

    return {
      scanned,
      shortlisted,
      aiEnriched,
      averageScore,
      topScore,
      batches: filteredRuns.length,
    };
  }, [filteredRuns, reportRows]);

  const statCards = [
    { label: "Scanned candidates", value: stats.scanned, icon: Users },
    { label: "Scan batches", value: stats.batches, icon: FileStack },
    { label: "Shortlisted", value: stats.shortlisted, icon: Star },
    { label: "Average score", value: `${stats.averageScore}%`, icon: BarChart3 },
  ];

  const reportTabs = [
    { key: "job", label: "Job Report" },
    { key: "batches", label: "Scan Batch Reports" },
  ];

  const selectedJobLabel = selectedJobId === "all"
    ? "All jobs"
    : jobs.find((job) => String(job.id) === String(selectedJobId))?.name || "Selected job";

  const csvEscape = (value) => {
    const text = Array.isArray(value) ? value.join("; ") : String(value ?? "");
    return `"${text.replace(/"/g, '""')}"`;
  };

  const getActiveReportExport = () => {
    if (activeTab === "batches") {
      return {
        title: "Scan Batch Report",
        headers: ["Job", "Scanned Date", "Candidates", "Average Score", "Top Score", "Shortlisted", "AI Enriched"],
        rows: filteredRuns.map((run) => [
          run.job?.name || "Untitled job",
          formatDate(run.scanned_at),
          run.total,
          `${Math.round(run.average_score || 0)}%`,
          `${Math.round(run.top_score || 0)}%`,
          run.shortlisted_count,
          run.ai_enriched_count,
        ]),
      };
    }

    return {
      title: "Job Candidate Report",
      headers: ["Candidate", "Job", "Scanned Date", "Score", "Status", "AI State", "Detected Skill Evidence", "Unconfirmed Skill Evidence"],
      rows: reportRows.map((candidate) => {
        const aiReady = candidate.ai_summary || (candidate.llm_strengths || []).length || (candidate.llm_gaps || []).length;
        return [
          candidate.filename,
          candidate.job_name,
          formatDate(candidate.scanned_at),
          `${Math.round(candidate.match_score || 0)}%`,
          candidate.is_shortlisted ? "Shortlisted" : candidate.is_rejected ? "Rejected" : "Needs review",
          aiReady ? "Enriched" : "Basic",
          candidate.matching_skills || [],
          candidate.missing_skills || [],
        ];
      }),
    };
  };

  const getReportFileName = (title, extension) => {
    const fileName = `${title}-${selectedJobLabel}-${range}`
      .replace(/[^a-z0-9]+/gi, "-")
      .replace(/^-|-$/g, "")
      .toLowerCase();

    return `${fileName || "recruitglance-report"}.${extension}`;
  };

  const downloadReportCsv = () => {
    const report = getActiveReportExport();
    if (!report.rows.length) {
      toast.error("No report rows available to export.");
      return;
    }

    const csv = [report.headers, ...report.rows]
      .map((row) => row.map(csvEscape).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = getReportFileName(report.title, "csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const downloadReportPdf = () => {
    const report = getActiveReportExport();
    if (!report.rows.length) {
      toast.error("No report rows available to export.");
      return;
    }

    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
    const rangeLabel = RANGE_OPTIONS.find((option) => option.value === range)?.label || "Selected range";

    doc.setFontSize(18);
    doc.text(report.title, 40, 42);
    doc.setFontSize(10);
    doc.setTextColor(82, 97, 116);
    doc.text(`${selectedJobLabel} | ${rangeLabel}`, 40, 60);
    doc.text(
      `Scanned: ${stats.scanned}   Batches: ${stats.batches}   Shortlisted: ${stats.shortlisted}   Average: ${stats.averageScore}%`,
      40,
      78
    );

    autoTable(doc, {
      startY: 100,
      head: [report.headers],
      body: report.rows.map((row) =>
        row.map((cell) => Array.isArray(cell) ? cell.join("; ") : String(cell ?? ""))
      ),
      styles: { fontSize: 8, cellPadding: 5, overflow: "linebreak" },
      headStyles: { fillColor: [4, 83, 61], textColor: [255, 255, 255] },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      margin: { left: 40, right: 40 },
    });

    doc.save(getReportFileName(report.title, "pdf"));
  };

  const mainContent = (
    <div className="space-y-6">
      <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-emerald-700">
              <ClipboardList size={14} />
              Hiring report
            </span>
            <h2 className="mt-4 text-2xl font-semibold text-slate-950">Recruiter Reports</h2>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-500">
              Review scan activity, shortlist movement, and job-level outcomes without rerunning scans.
            </p>
          </div>

          <div className="grid w-full gap-3 sm:grid-cols-2 lg:w-[520px]">
            <label>
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Date range</span>
              <select
                value={range}
                onChange={(event) => setRange(event.target.value)}
                className="mt-2 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
              >
                {RANGE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>

            <label>
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Job</span>
              <select
                value={selectedJobId}
                onChange={(event) => setSelectedJobId(event.target.value)}
                className="mt-2 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
              >
                <option value="all">All jobs</option>
                {jobs.map((job) => (
                  <option key={job.id} value={job.id}>
                    {job.name}{job.is_archived ? " (Archived)" : ""}
                  </option>
                ))}
              </select>
            </label>

            <div className="flex gap-2 sm:col-span-2">
              <button
                type="button"
                onClick={downloadReportCsv}
                disabled={fetching}
                className="inline-flex min-h-[40px] flex-1 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Download size={16} />
                CSV
              </button>
              <button
                type="button"
                onClick={downloadReportPdf}
                disabled={fetching}
                className="inline-flex min-h-[40px] flex-1 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <FileText size={16} />
                PDF
              </button>
            </div>
          </div>
        </div>
      </section>

      {fetching ? (
        <section className="rounded-lg border border-slate-200 bg-white p-8 text-center text-slate-500 shadow-sm">
          Loading reports...
        </section>
      ) : (
        <>
          <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {statCards.map((card) => {
              const Icon = card.icon;
              return (
                <div key={card.label} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{card.label}</p>
                      <p className="mt-3 text-3xl font-semibold text-slate-950">{card.value}</p>
                    </div>
                    <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700">
                      <Icon size={21} />
                    </div>
                  </div>
                </div>
              );
            })}
          </section>

          <section className="rounded-lg border border-slate-200 bg-white p-2 shadow-sm">
            <div className="grid gap-2 md:grid-cols-2">
              {reportTabs.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  className={`min-h-[42px] rounded-md px-4 text-sm font-semibold transition ${
                    activeTab === tab.key
                      ? "bg-emerald-950 text-white shadow-sm"
                      : "bg-slate-50 text-slate-600 hover:bg-emerald-50 hover:text-emerald-900"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </section>

          {activeTab === "job" && (
            <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-slate-950">Job Report</h2>
                  <p className="mt-1 text-sm leading-6 text-slate-500">
                    All candidates from the selected report window with scanned date, score, shortlist status, and AI readiness.
                  </p>
                </div>
                <span className="w-fit rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-sm font-semibold text-slate-700">
                  {reportRows.length} candidates
                </span>
              </div>

              <div className="mt-5 overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-xs font-semibold uppercase tracking-wide text-slate-400">
                      <th className="py-3 pr-4">Candidate</th>
                      <th className="py-3 pr-4">Job</th>
                      <th className="py-3 pr-4">Scanned date</th>
                      <th className="py-3 pr-4">Score</th>
                      <th className="py-3 pr-4">Status</th>
                      <th className="py-3 pr-4">AI</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportRows.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="py-8 text-center text-slate-500">
                          No candidates found for this report filter.
                        </td>
                      </tr>
                    ) : (
                      reportRows
                        .sort((a, b) => new Date(b.scanned_at) - new Date(a.scanned_at))
                        .map((candidate) => {
                          const aiReady = candidate.ai_summary || (candidate.llm_strengths || []).length || (candidate.llm_gaps || []).length;

                          return (
                            <tr key={`report-row-${candidate.scan_id}`} className="border-b border-slate-100">
                              <td className="max-w-[260px] py-4 pr-4">
                                <span className="block truncate font-semibold text-slate-950">{candidate.filename}</span>
                                <span className="mt-1 block truncate text-xs text-slate-500">
                                  {(candidate.matching_skills || []).slice(0, 4).join(", ") || "No matched skills shown"}
                                </span>
                              </td>
                              <td className="max-w-[220px] py-4 pr-4">
                                <span className="block truncate text-slate-600">{candidate.job_name}</span>
                              </td>
                              <td className="py-4 pr-4 text-slate-600">{formatDate(candidate.scanned_at)}</td>
                              <td className="py-4 pr-4">
                                <span className={`rounded-full border px-2 py-1 text-xs font-bold ${scoreTone(candidate.match_score)}`}>
                                  {Math.round(candidate.match_score || 0)}%
                                </span>
                              </td>
                              <td className="py-4 pr-4 text-slate-600">
                                {candidate.is_shortlisted ? "Shortlisted" : candidate.is_rejected ? "Rejected" : "Needs review"}
                              </td>
                              <td className="py-4 pr-4">
                                <span className={`rounded-full px-2 py-1 text-xs font-semibold ${
                                  aiReady ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"
                                }`}>
                                  {aiReady ? "Enriched" : "Basic"}
                                </span>
                              </td>
                            </tr>
                          );
                        })
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {activeTab === "batches" && (
            <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-lg font-semibold text-slate-950">Scan Batch Report</h2>
                  <p className="mt-1 text-sm leading-6 text-slate-500">
                    Job-level scan batches in the selected period.
                  </p>
                </div>
                <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-sm font-semibold text-slate-700">
                  Top {stats.topScore}%
                </span>
              </div>

              <div className="mt-5 space-y-3">
                {filteredRuns.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-slate-200 p-6 text-center text-sm text-slate-500">
                    No scan batches found for this filter.
                  </div>
                ) : (
                  filteredRuns.slice(0, 10).map((run) => (
                    <button
                      key={run.history_id}
                      type="button"
                      onClick={() => navigate("/scan-history")}
                      className="w-full rounded-lg border border-slate-200 bg-slate-50 p-4 text-left transition hover:border-emerald-300 hover:bg-emerald-50/60"
                    >
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div className="min-w-0">
                          <p className="truncate text-base font-semibold text-slate-950">{run.job?.name || "Untitled job"}</p>
                          <p className="mt-1 text-sm text-slate-500">
                            {formatDate(run.scanned_at)} | {run.total} candidates | {run.shortlisted_count} shortlisted
                          </p>
                        </div>
                        <span className={`w-fit rounded-full border px-3 py-1 text-sm font-bold ${scoreTone(run.top_score)}`}>
                          Top {Math.round(run.top_score || 0)}%
                        </span>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );

  if (loading || !user) return null;

  return mainContent;
}

export default function Reports() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [user, loading, navigate]);

  if (loading || !user) return null;

  const leftPanel = <RecruiterSidebar activeItem="reports" user={user} />;

  return (
    <DashboardLayout
      user={user}
      eyebrow="Hiring insights"
      title="Reports"
      subtitle="Summarize scan batches, shortlisted candidates, and recruiter outcomes"
      leftPanel={leftPanel}
      mainContent={<ReportsPanel />}
    />
  );
}
